import { useCallback, useMemo } from "react";
import { CapabilityDetailLayout } from "@/components/capability-detail/CapabilityDetailLayout";
import {
  useFileRevisionEditor,
  type FileResolverArgs,
} from "@/hooks/use-file-revision-editor";
import { DecisionEditor } from "@/components/admin/decisions/DecisionEditor";
import {
  DecisionCapabilityDocumentSchema,
  createEmptyDocument,
} from "@/types/decision-definition-schema";
import type {
  DecisionDefinition,
  DecisionEditorState,
} from "@/types/decision-definition-schema";

function resolveDecisionFilePath({
  files,
  typeConfig,
}: FileResolverArgs): string {
  const defs = typeConfig?.definitions;
  if (Array.isArray(defs)) {
    for (const d of defs) {
      if (typeof d !== "string") continue;
      const exact = files.find((f) => f.path === d);
      if (exact) return exact.path;
      const bySuffix = files.find(
        (f) => f.path.endsWith(d) || f.path.endsWith(`/${d}`)
      );
      if (bySuffix) return bySuffix.path;
    }
  }
  const byName = files.find((f) => f.path.endsWith("decision.json"));
  if (byName) return byName.path;
  if (files.length > 0) {
    const json = files.find((f) => /\.json$/i.test(f.path));
    if (json) return json.path;
    return files[0].path;
  }
  return "decision.json";
}

export interface DecisionDetailPageProps {
  appKey?: string;
  name?: string;
  headerLayerId?: string;
}

export default function DecisionDetailPage({
  appKey: appKeyProp,
  name: nameProp,
  headerLayerId,
}: DecisionDetailPageProps = {}) {
  const editor = useFileRevisionEditor({
    resolveFilePath: resolveDecisionFilePath,
    primaryContentType: "application/json",
    logPrefix: "decision-detail-page",
    saveSuccessMessage: "Decision saved",
    saveErrorMessage: "Failed to save decision",
    appKey: appKeyProp,
    capabilityName: nameProp,
  });

  const parsed = useMemo(() => {
    if (!editor.displayedValue) return null;
    try {
      const raw = JSON.parse(editor.displayedValue);
      const result = DecisionCapabilityDocumentSchema.safeParse(raw);
      if (result.success) return result.data;

      if (raw.definition) {
        return {
          definition: raw.definition as DecisionDefinition,
          editorState: (raw.editorState as DecisionEditorState) || undefined,
        };
      }

      return null;
    } catch {
      return null;
    }
  }, [editor.displayedValue]);

  const handleChange = useCallback(
    (def: DecisionDefinition, editorState?: DecisionEditorState) => {
      const doc = { definition: def, editorState };
      editor.setDraft(JSON.stringify(doc, null, 2));
    },
    [editor]
  );

  const readOnly =
    editor.isArchived || editor.isViewingPublished || !editor.hasWorkingRevision;

  const document =
    parsed || createEmptyDocument(nameProp || "new-decision", nameProp || "New Decision");

  return (
    <CapabilityDetailLayout
      typeLabel="Decision"
      appKey={appKeyProp}
      capabilityName={nameProp}
      supplementalDeferredSave={editor.supplementalDeferredSave}
      selectedRevisionId={editor.selectedRevisionId}
      onRevisionChange={editor.setSelectedRevisionId}
      headerLayerId={headerLayerId}
      fullBleed
    >
      <DecisionEditor
        definition={document.definition}
        editorState={document.editorState}
        onChange={handleChange}
        readOnly={readOnly}
        isLoading={editor.displayedLoading}
      />
    </CapabilityDetailLayout>
  );
}
